// Cookie consent: storage, and loaders for every non-essential tracker.
// Nothing in this file runs until a visitor has made a choice.

export type ConsentChoices = {
  analytics: boolean
  marketing: boolean
}

type StoredConsent = ConsentChoices & {
  version: number
  updatedAt: string
}

const STORAGE_KEY = 'mlp_cookie_consent'
// Bump when trackers change so every visitor is asked again.
const CONSENT_VERSION = 1
// Consent is re-requested after 12 months, in line with ICO guidance.
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000

export const OPEN_PREFERENCES_EVENT = 'mlp:open-cookie-preferences'

const GA_MEASUREMENT_ID = 'G-HT5Z5GWWNY'
const CLARITY_PROJECT_ID = 'yr2k2vgu7d'
const LINKEDIN_PARTNER_ID = '9721298'

export function readConsent(): ConsentChoices | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredConsent
    if (stored.version !== CONSENT_VERSION) return null
    if (Date.now() - new Date(stored.updatedAt).getTime() > CONSENT_MAX_AGE_MS) {
      return null
    }
    return { analytics: stored.analytics, marketing: stored.marketing }
  } catch {
    return null
  }
}

export function saveConsent(choices: ConsentChoices) {
  const previous = readConsent()
  const stored: StoredConsent = {
    ...choices,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Storage unavailable (private mode): choice applies to this visit only.
  }
  // Withdrawing consent: scripts already running cannot be unloaded, so clear
  // their first-party cookies and reload to stop them.
  const withdrewAnalytics = previous?.analytics && !choices.analytics
  const withdrewMarketing = previous?.marketing && !choices.marketing
  if (withdrewAnalytics || withdrewMarketing) {
    if (withdrewAnalytics) clearCookies([/^_ga/, /^_clck$/, /^_clsk$/])
    if (withdrewMarketing) clearCookies([/^li_/, /^_li/])
    window.location.reload()
    return
  }
  applyConsent(choices)
}

export function applyConsent(choices: ConsentChoices) {
  if (choices.analytics) {
    loadGoogleAnalytics()
    loadClarity()
  }
  if (choices.marketing) {
    loadLinkedInInsightTag()
  }
}

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT))
}

function injectInlineScript(id: string, code: string, target: HTMLElement) {
  if (document.getElementById(id)) return false
  const script = document.createElement('script')
  script.id = id
  script.type = 'text/javascript'
  script.textContent = code
  target.appendChild(script)
  return true
}

function loadGoogleAnalytics() {
  if (document.getElementById('gtag-script')) return
  const gtagScript = document.createElement('script')
  gtagScript.id = 'gtag-script'
  gtagScript.async = true
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(gtagScript)
  injectInlineScript(
    'gtag-inline',
    `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', { anonymize_ip: true });
    `,
    document.head,
  )
}

function loadClarity() {
  injectInlineScript(
    'clarity-init',
    `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
      window.clarity("consent");
    `,
    document.head,
  )
}

function loadLinkedInInsightTag() {
  const injected = injectInlineScript(
    'linkedin-insight-init',
    `
      _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `,
    document.body,
  )
  if (!injected) return
  injectInlineScript(
    'linkedin-insight-loader',
    `
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
    `,
    document.body,
  )
}

function clearCookies(patterns: RegExp[]) {
  const host = window.location.hostname
  const rootDomain = host.split('.').slice(-3).join('.')
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (!patterns.some((pattern) => pattern.test(name))) return
    const expiry = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    document.cookie = expiry
    document.cookie = `${expiry}; domain=${host}`
    document.cookie = `${expiry}; domain=.${rootDomain}`
  })
}
